import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logo from '../assets/logo.png';
import { PencilIcon, TrashIcon, PlusIcon, ArrowPathIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../context/AuthContext';

// API Configuration
const API_URL = (process.env.REACT_APP_API_BASE_URL || 'https://auditoria.tramites.notaria4otavalo.com.ec') + '/api/audit/configure-commission/';
const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const message = error.response?.data?.error || 'Error en la solicitud al servidor.';
    toast.error(message, { position: 'top-right', autoClose: 3000 });
    return Promise.reject(error);
  }
);

// Commission Value Row Component
const CommissionValueRow = React.memo(({ value, onChange, onRemove, canRemove, loading }) => (
  <motion.tr
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    transition={{ duration: 0.3 }}
    className="border-b border-notaria-blue-2 hover:bg-notaria-blue-2"
  >
    <td className="p-3" role="cell">
      <input
        type="number"
        min="0"
        step="0.01"
        value={value.value}
        onChange={(e) => onChange('value', e.target.value)}
        className="w-24 p-2 rounded-lg bg-notaria-blue-3 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
        disabled={loading}
        aria-label={`Valor`}
      />
    </td>
    <td className="p-3" role="cell">
      <input
        type="date"
        value={value.start_date}
        onChange={(e) => onChange('start_date', e.target.value)}
        className="w-40 p-2 rounded-lg bg-notaria-blue-3 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
        disabled={loading}
        aria-label={`Fecha de inicio`}
      />
    </td>
    <td className="p-3" role="cell">
      <input
        type="date"
        value={value.end_date || ''}
        onChange={(e) => onChange('end_date', e.target.value || null)}
        className="w-40 p-2 rounded-lg bg-notaria-blue-3 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
        disabled={loading}
        aria-label={`Fecha de fin`}
      />
    </td>
    <td className="p-3" role="cell">
      {canRemove && (
        <motion.button
          onClick={onRemove}
          disabled={loading}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          aria-label={`Eliminar valor adicional`}
          className="text-red-500 hover:text-red-700"
        >
          <TrashIcon className="w-5 h-5" />
        </motion.button>
      )}
    </td>
  </motion.tr>
));

const ConfigureCommission = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth() || { user: null, loading: true };
  const [commissions, setCommissions] = useState([]);
  const [newValues, setNewValues] = useState({});
  const [newCommission, setNewCommission] = useState({ name: '', description: '', defaultValue: '', startDate: '' });
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formErrors, setFormErrors] = useState({ name: '', defaultValue: '', startDate: '' });
  const [page, setPage] = useState(1);
  const [usePagination, setUsePagination] = useState(false);
  const cancelButtonRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    if (!authLoading && (!user || !user.is_notario)) {
      toast.error('No tienes permisos para acceder a esta página.', {
        position: 'top-right',
        autoClose: 3000,
      });
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  const pageSize = 10;
  const totalPages = useMemo(() => Math.max(1, Math.ceil(commissions.length / pageSize)), [commissions]);
  const displayedCommissions = useMemo(
    () => (usePagination ? commissions.slice((page - 1) * pageSize, page * pageSize) : commissions),
    [commissions, page, usePagination]
  );

  const fetchCommissions = useCallback(async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const { data } = await api.get('');
      setCommissions(data.commissions || []);
      setNewValues({});
      setPage(1);
      setUsePagination((data.commissions || []).length > pageSize);
    } catch (error) {
      const message = error.response?.data?.error || 'No se pudo cargar las comisiones. Por favor, intenta de nuevo.';
      toast.error(message, { position: 'top-right', autoClose: 5000 });
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user && user.is_notario) {
      fetchCommissions();
    }
  }, [fetchCommissions, user, authLoading]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && deleteTarget) setDeleteTarget(null);
      if (e.key === 'Tab' && deleteTarget) {
        e.preventDefault();
        const focusable = modalRef.current?.querySelectorAll('button:not(:disabled)');
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) last.focus();
        else if (!e.shiftKey && document.activeElement === last) first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    if (deleteTarget && cancelButtonRef.current) cancelButtonRef.current.focus();
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [deleteTarget]);

  const hasChanges = useMemo(() => Object.keys(newValues).length > 0, [newValues]);

  const validateNewCommission = useCallback(() => {
    const errors = { name: '', defaultValue: '', startDate: '' };
    if (!newCommission.name.trim()) errors.name = 'El nombre es requerido.';
    const val = parseFloat(newCommission.defaultValue);
    if (!newCommission.defaultValue || isNaN(val) || val < 0) errors.defaultValue = 'Debe ser un número positivo.';
    if (!newCommission.startDate) errors.startDate = 'La fecha de inicio es requerida.';
    setFormErrors(errors);
    return !errors.name && !errors.defaultValue && !errors.startDate;
  }, [newCommission]);

  const handleValueChange = (commissionId, index, field, value) => {
    setNewValues((prev) => {
      const values = prev[commissionId] || [...(commissions.find(c => c.id === commissionId)?.values || [])];
      values[index] = { ...values[index], [field]: value };
      return { ...prev, [commissionId]: values };
    });
  };

  const handleAddValue = (commissionId) => {
    setNewValues((prev) => {
      const values = prev[commissionId] || [...(commissions.find(c => c.id === commissionId)?.values || [])];
      values.push({ value: '', start_date: '', end_date: null });
      return { ...prev, [commissionId]: values };
    });
  };

  const handleRemoveValue = (commissionId, index) => {
    setNewValues((prev) => {
      const values = prev[commissionId] || [...(commissions.find(c => c.id === commissionId)?.values || [])];
      if (values.length <= 1) return prev; // No permitir eliminar si solo hay un valor
      values.splice(index, 1);
      return { ...prev, [commissionId]: values };
    });
  };

  const handleSave = async () => {
    if (!hasChanges) {
      toast.warn('No hay cambios para guardar.', { position: 'top-right' });
      return;
    }
    const payload = {
      commissions: Object.entries(newValues).map(([id, values]) => ({
        id: parseInt(id),
        values: values.map(v => ({
          id: v.id || null,
          value: parseFloat(v.value),
          start_date: v.start_date,
          end_date: v.end_date,
        })),
      })),
    };
    if (payload.commissions.some(c => c.values.length === 0)) {
      toast.error('Cada comisión debe tener al menos un valor.', { position: 'top-right' });
      return;
    }
    if (payload.commissions.some(c => c.values.some(v => isNaN(v.value) || v.value < 0 || !v.start_date))) {
      toast.error('Todos los valores deben ser números positivos y tener fecha de inicio.', { position: 'top-right' });
      return;
    }
    setLoading(true);
    try {
      await api.post('', payload);
      toast.success('Comisiones actualizadas exitosamente.', { position: 'top-right' });
      fetchCommissions();
    } catch (error) {
      const message = error.response?.data?.error || 'Error al guardar los cambios. Por favor, intenta de nuevo.';
      toast.error(message, { position: 'top-right', autoClose: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!validateNewCommission()) {
      toast.warn('Por favor, corrige los errores en el formulario.', { position: 'top-right' });
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.put('', {
        name: newCommission.name.trim(),
        description: newCommission.description.trim(),
        values: [{ value: parseFloat(newCommission.defaultValue), start_date: newCommission.startDate, end_date: null }],
      });
      toast.success('Nueva comisión creada exitosamente.', { position: 'top-right' });
      setNewCommission({ name: '', description: '', defaultValue: '', startDate: '' });
      setFormErrors({ name: '', defaultValue: '', startDate: '' });
      setCommissions([...commissions, data.commission]);
    } catch (error) {
      const message = error.response?.data?.error || 'Error al crear la nueva comisión. Por favor, intenta de nuevo.';
      toast.error(message, { position: 'top-right', autoClose: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id, name) => setDeleteTarget({ id, name });

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setLoading(true);
    try {
      await api.delete('', { data: { commission_id: deleteTarget.id } });
      toast.success(`Comisión "${deleteTarget.name}" eliminada exitosamente.`, { position: 'top-right' });
      setCommissions(commissions.filter((c) => c.id !== deleteTarget.id));
    } catch (error) {
      const message = error.response?.data?.error || 'Error al eliminar la comisión. Por favor, intenta de nuevo.';
      toast.error(message, { position: 'top-right', autoClose: 5000 });
    } finally {
      setLoading(false);
      setDeleteTarget(null);
    }
  };

  if (authLoading) return <div className="text-white text-center">Cargando...</div>;
  if (!user) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="min-h-screen flex flex-col text-white p-4 sm:p-6 bg-gray-900"
    >
      <header className="flex justify-between items-center mb-6 sm:mb-8">
        <div className="flex items-center">
          <img src={logo} alt="Logo Notaría 4 Cantón Otavalo" className="w-12 h-auto mr-3 sm:w-16 sm:mr-4" />
          <h1 className="text-2xl sm:text-3xl font-bold">Configurar Comisiones</h1>
        </div>
        <div className="flex items-center space-x-4">
          <motion.button
            onClick={fetchCommissions}
            disabled={loading}
            className="bg-notaria-blue-2 text-white py-2 px-4 rounded-full hover:bg-notaria-yellow hover:text-notaria-blue-1 transition-all"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Refrescar comisiones"
          >
            <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </motion.button>
          <Link
            to="/dashboard/notario"
            className="bg-notaria-yellow text-notaria-blue-1 font-bold py-2 px-4 rounded-full hover:bg-notaria-red hover:text-white transition-all"
          >
            Volver al Dashboard
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto space-y-6">
        {/* Existing Commissions */}
        <div className="bg-notaria-blue-1 bg-opacity-80 p-4 sm:p-6 rounded-xl shadow-lg">
          <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">Comisiones Actuales</h2>
          {fetchError && (
            <div className="text-center mb-4">
              <p className="text-red-500">Error al cargar comisiones.</p>
              <motion.button
                onClick={fetchCommissions}
                className="mt-2 bg-notaria-yellow text-notaria-blue-1 font-bold py-2 px-4 rounded-lg hover:bg-notaria-red hover:text-white transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Reintentar
              </motion.button>
            </div>
          )}
          {loading && !fetchError && <p className="text-center text-lg">Cargando...</p>}
          {!loading && commissions.length === 0 && (
            <p className="text-center text-lg">No hay comisiones configuradas.</p>
          )}
          {!loading && commissions.length > 0 && (
            <>
              <div className="flex justify-between items-center mb-4">
                <button
                  onClick={() => setUsePagination(!usePagination)}
                  className="text-notaria-yellow hover:underline"
                >
                  {usePagination ? 'Mostrar Todo' : 'Paginado'}
                </button>
              </div>
              <div className="overflow-x-auto">
                {displayedCommissions.map((commission) => {
                  const values = newValues[commission.id] || commission.values;
                  const canRemove = values.length > 1; // Permitir eliminación solo si hay más de un valor
                  return (
                    <div key={commission.id} className="mb-6">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="text-lg font-semibold">{commission.name}</h3>
                        {commission.name !== 'Reconocimiento de Firmas de Vehículo' && (
                          <motion.button
                            onClick={() => handleDelete(commission.id, commission.name)}
                            disabled={loading}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            aria-label={`Eliminar comisión ${commission.name}`}
                          >
                            <TrashIcon className="w-5 h-5 text-red-500 hover:text-red-700" />
                          </motion.button>
                        )}
                      </div>
                      <p className="mb-2">{commission.description?.trim() || '-'}</p>
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-notaria-blue-2 text-white">
                            <th className="p-3 text-left">Valor ($)</th>
                            <th className="p-3 text-left">Fecha Inicio</th>
                            <th className="p-3 text-left">Fecha Fin</th>
                            <th className="p-3 text-left">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          <AnimatePresence>
                            {values.map((value, index) => (
                              <CommissionValueRow
                                key={value.id || `new-${index}`}
                                value={value}
                                onChange={(field, val) => handleValueChange(commission.id, index, field, val)}
                                onRemove={() => handleRemoveValue(commission.id, index)}
                                canRemove={canRemove}
                                loading={loading}
                              />
                            ))}
                          </AnimatePresence>
                        </tbody>
                      </table>
                      <motion.button
                        onClick={() => handleAddValue(commission.id)}
                        disabled={loading}
                        className="mt-2 bg-notaria-green text-white py-1 px-3 rounded-lg hover:bg-notaria-yellow hover:text-notaria-blue-1 transition-all"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <PlusIcon className="w-5 h-5 inline mr-2" /> Agregar Valor
                      </motion.button>
                    </div>
                  );
                })}
              </div>
              {usePagination && totalPages > 1 && (
                <div className="flex justify-center items-center space-x-4 mt-4">
                  <motion.button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1 || loading}
                    className="p-2 bg-notaria-blue-2 rounded-full hover:bg-notaria-yellow hover:text-notaria-blue-1 disabled:opacity-50"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label="Página anterior"
                  >
                    <ChevronLeftIcon className="w-5 h-5" />
                  </motion.button>
                  <span>Página {page} de {totalPages}</span>
                  <motion.button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages || loading}
                    className="p-2 bg-notaria-blue-2 rounded-full hover:bg-notaria-yellow hover:text-notaria-blue-1 disabled:opacity-50"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label="Página siguiente"
                  >
                    <ChevronRightIcon className="w-5 h-5" />
                  </motion.button>
                </div>
              )}
              <motion.button
                onClick={handleSave}
                disabled={!hasChanges || loading}
                className={`mt-4 w-full bg-notaria-yellow text-notaria-blue-1 font-bold py-2 rounded-lg hover:bg-notaria-red hover:text-white transition-all ${!hasChanges || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                whileHover={{ scale: hasChanges && !loading ? 1.02 : 1 }}
                whileTap={{ scale: hasChanges && !loading ? 0.98 : 1 }}
              >
                {loading ? (
                  <ArrowPathIcon className="w-5 h-5 inline mr-2 animate-spin" />
                ) : (
                  <PencilIcon className="w-5 h-5 inline mr-2" />
                )}
                Guardar Cambios
              </motion.button>
            </>
          )}
        </div>

        {/* Create New Commission */}
        <div className="bg-notaria-blue-1 bg-opacity-80 p-4 sm:p-6 rounded-xl shadow-lg">
          <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">Crear Nueva Comisión</h2>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm sm:text-lg font-semibold mb-2">Nombre</label>
                <input
                  id="name"
                  type="text"
                  value={newCommission.name}
                  onChange={(e) => setNewCommission((prev) => ({ ...prev, name: e.target.value }))}
                  className={`w-full p-2 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow ${formErrors.name ? 'border-red-500 border-2' : ''}`}
                  required
                  aria-invalid={!!formErrors.name}
                  aria-describedby={formErrors.name ? 'name-error' : undefined}
                />
                {formErrors.name && <p id="name-error" className="text-red-500 text-sm mt-1">{formErrors.name}</p>}
              </div>
              <div>
                <label htmlFor="defaultValue" className="block text-sm sm:text-lg font-semibold mb-2">Valor Predeterminado ($)</label>
                <input
                  id="defaultValue"
                  type="number"
                  min="0"
                  step="0.01"
                  value={newCommission.defaultValue}
                  onChange={(e) => setNewCommission((prev) => ({ ...prev, defaultValue: e.target.value }))}
                  className={`w-full p-2 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow ${formErrors.defaultValue ? 'border-red-500 border-2' : ''}`}
                  required
                  aria-invalid={!!formErrors.defaultValue}
                  aria-describedby={formErrors.defaultValue ? 'value-error' : undefined}
                />
                {formErrors.defaultValue && <p id="value-error" className="text-red-500 text-sm mt-1">{formErrors.defaultValue}</p>}
              </div>
            </div>
            <div>
              <label htmlFor="startDate" className="block text-sm sm:text-lg font-semibold mb-2">Fecha de Inicio</label>
              <input
                id="startDate"
                type="date"
                value={newCommission.startDate}
                onChange={(e) => setNewCommission((prev) => ({ ...prev, startDate: e.target.value }))}
                className={`w-full p-2 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow ${formErrors.startDate ? 'border-red-500 border-2' : ''}`}
                required
                aria-invalid={!!formErrors.startDate}
                aria-describedby={formErrors.startDate ? 'startdate-error' : undefined}
              />
              {formErrors.startDate && <p id="startdate-error" className="text-red-500 text-sm mt-1">{formErrors.startDate}</p>}
            </div>
            <div>
              <label htmlFor="description" className="block text-sm sm:text-lg font-semibold mb-2">Descripción (Opcional)</label>
              <textarea
                id="description"
                rows="3"
                value={newCommission.description}
                onChange={(e) => setNewCommission((prev) => ({ ...prev, description: e.target.value }))}
                className="w-full p-2 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
              />
            </div>
            <motion.button
              type="submit"
              disabled={loading}
              className={`w-full bg-notaria-green text-white font-bold py-2 rounded-lg hover:bg-notaria-yellow hover:text-notaria-blue-1 transition-all ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              whileHover={{ scale: !loading ? 1.02 : 1 }}
              whileTap={{ scale: !loading ? 0.98 : 1 }}
            >
              {loading ? (
                <ArrowPathIcon className="w-5 h-5 inline mr-2 animate-spin" />
              ) : (
                <PlusIcon className="w-5 h-5 inline mr-2" />
              )}
              Crear Comisión
            </motion.button>
          </form>
        </div>
      </main>

      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            ref={modalRef}
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              className="bg-notaria-blue-1 text-white rounded-lg p-6 max-w-sm"
            >
              <h3 id="modal-title" className="text-xl font-semibold mb-4">Confirmar Eliminación</h3>
              <p id="modal-description" className="mb-6">¿Estás seguro de que deseas eliminar la comisión "{deleteTarget.name}"?</p>
              <div className="flex justify-end space-x-4">
                <button
                  ref={cancelButtonRef}
                  onClick={() => setDeleteTarget(null)}
                  className="px-4 py-2 rounded bg-notaria-blue-2 hover:bg-notaria-blue-3 text-white"
                  aria-label="Cancelar eliminación"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={loading}
                  className="px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600 inline-flex items-center"
                  aria-label="Confirmar eliminación"
                >
                  {loading ? <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" /> : 'Eliminar'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ToastContainer position="top-right" autoClose={3000} pauseOnHover />
    </motion.div>
  );
};

export default ConfigureCommission;